import { showsLicenceCountdown } from '../../api/roles';
import type { LicenceSnapshot, UserRole } from '../../api/types';
import { useT } from '../../i18n/i18n.tsx';
import { Button } from '../../ui';
import { useLicenceCountdown } from './useLicenceCountdown';
import './LicenceExpiryBar.css';

/**
 * MVP-513. The venue's licence countdown: a strip across the top of the console from seven days
 * before `expiresAt`, every day, through the date itself and through the week of grace after it.
 *
 * === WHY THIS SURFACE ===
 * The console shell, as a sibling of the outage strip and the storefront strip. It is not a toast:
 * a toast belongs to a control someone just used, and this belongs to the whole window, on whichever
 * of the six screens happens to be up when the venue's day starts. It is not on the Cockpit either —
 * the KJ may spend the night in Library, and "every day" has to mean "whatever they are looking at".
 * The slot matters as well as the strip: `App.css`'s one chrome-hiding rule deliberately spares this
 * row of the shell, so an unattended room PC showing only the document still shows the countdown.
 *
 * === WHO SEES IT ===
 * The venue and the person standing at its machine, and nobody else. `showsLicenceCountdown` is the
 * line — see its doc comment for why an admin gets a different sentence on a different screen — and
 * the guest never enters the question at all: no guest surface is ever handed a licence, which
 * `licenceExpiryBar.test.ts` asserts against the guest sources themselves. A guest asking for a song
 * is not party to the contract, and an expiry notice on their phone is an invitation to ask the KJ
 * awkward questions in front of a room.
 *
 * ONE ACTION, AND ONLY FOR WHO CAN TAKE IT. MVP-278 gave the shop a way to renew on its own (the
 * plans page: PromptPay + slip), so the strip carries a ต่ออายุ button — but only when `App` hands it
 * `onRenew`, which it does for `canPayForPlan` alone. A KJ at the same machine cannot pay for the
 * shop, so for them the copy still names who to contact and stops there: a control that only
 * apologises is worse than none.
 *
 * === THE DISMISS CONTROL ===
 * Dismissible for the current Bangkok day only, per machine (`licenceCountdown.ts` holds the storage
 * and its reasoning). A strip that cannot be dismissed at all gets covered, ignored or worked
 * around; one that can be dismissed forever is dismissed on day 7 and never seen again, which is the
 * exact failure the owner asked us to avoid. `ui/Button` rather than a bare `<button>` so the ≥44px
 * coarse-pointer height comes from `pointerTouchTargets.test.ts`'s own rule and not from a raw px in
 * this component's stylesheet.
 *
 * `role="status"`, not `alert`: nothing interrupted anyone. This is a standing fact about the
 * night, the same choice the two strips beside it make.
 */
export function LicenceExpiryBar({
  licence,
  role,
  onRenew,
}: {
  licence: LicenceSnapshot | null;
  role: UserRole | null;
  /** MVP-278: opens the plans page. Absent for a role that cannot pay. */
  onRenew?: () => void;
}) {
  const t = useT();
  // Gated by handing the hook nothing rather than by an early return: hooks cannot be called
  // conditionally, and an admin must not even accumulate the day-watcher's state.
  const { view, dismiss } = useLicenceCountdown(showsLicenceCountdown(role) ? licence : null);
  if (!view) return null;
  return (
    <div className={`licence-bar licence-bar--${view.tone}`} role="status" data-testid="licence-expiry-bar">
      <strong className="licence-bar__title">{t(view.message.key, view.message.params)}</strong>
      {/* The customer name is the venue's own name off the licence row, not copy — a shop with two
          machines in two rooms is being told WHICH contract this is. */}
      {view.customerName && <span className="licence-bar__customer">{view.customerName}</span>}
      {onRenew ? (
        <Button variant="primary" size="sm" onClick={onRenew}>
          {t('ops.licence.renew')}
        </Button>
      ) : (
        <span>{t('ops.licence.contact')}</span>
      )}
      <Button variant="secondary" size="sm" onClick={dismiss}>
        {t('ops.licence.dismissToday')}
      </Button>
    </div>
  );
}
